import { useChat } from "@ai-sdk/react"
import { Copy, Link, MoreVertical, RefreshCw, Send, Settings } from "lucide-react"
import React, { useEffect, useRef, useState } from "react"
import toast from "react-hot-toast"
import { trpc } from "~/lib/trpc"

import { MessageActions } from "./MessageActions"
import { PersonaAvatar } from "./PersonaAvatar"
import { StreamingIndicator } from "./StreamingIndicator"

interface Conversation {
  id: string
  title?: string
  conversation_type: string
  primary_persona: string
  project_id: string
  todoist_task_id?: string
  created_at: Date
  updated_at: Date
  last_message_at?: Date
  message_count: number
  metadata?: any
  is_pinned: boolean
  is_archived: boolean
  tags: string[]
  project_name?: string
  project_color?: string
  personas?: any[]
  tools?: any[]
  messages?: any[]
}

interface MainChatAreaProps {
  conversation: Conversation | null
  loading: boolean
  onUpdateConversation: (id: string, updates: any) => void
  onNewConversation: () => void
}

// Simple message interface
interface SimpleMessage {
  id: string
  role: "user" | "assistant"
  content: string
  timestamp: Date
}

export const MainChatArea: React.FC<MainChatAreaProps> = ({
  conversation,
  loading,
  onUpdateConversation,
  onNewConversation,
}) => {
  const [showSettings, setShowSettings] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const sendMessageMutation = trpc.aiChat.sendMessage.useMutation()

  // Use AI SDK's useChat hook for proper streaming
  const [input, setInput] = useState("")
  const { messages, error, status, stop, sendMessage, regenerate } = useChat({
    onError: (error) => {
      console.error("Chat error:", error)
      toast.error("Failed to send message")
    },
    onFinish: (message) => {
      console.log("Message finished:", message)
      // Optionally invalidate conversation queries to refresh UI
      // utils.aiChat.getConversation.invalidate()
    },
  })

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      e.preventDefault()
      if (input.trim()) {
        void sendMessage({ role: "user", text: input })
        setInput("")
      }
    }
  }

  const copyMessage = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content)
      toast.success("Message copied to clipboard!")
    } catch (error) {
      toast.error("Failed to copy message")
    }
  }

  const regenerateMessage = (messageId: string) => {
    // Use the regenerate function from useChat to regenerate the last response
    void regenerate()
    toast.success("Regenerating response...")
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <div className="text-muted-foreground">Loading conversation...</div>
        </div>
      </div>
    )
  }

  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">🤖</div>
          <h2 className="text-xl font-semibold mb-2">Welcome to AI Chat Hub</h2>
          <p className="text-muted-foreground mb-6">
            Select a conversation from the sidebar or create a new one to get started.
          </p>
          <button
            onClick={onNewConversation}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Start New Conversation
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Chat Header */}
      <div className="p-4 border-b border-border bg-background">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <PersonaAvatar persona={conversation.primary_persona} size="md" />
            <div>
              <h1 className="font-semibold text-lg">
                {conversation.title || `${conversation.conversation_type} Chat`}
              </h1>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>🤖 {conversation.primary_persona}</span>
                <span>•</span>
                <span>{conversation.message_count} messages</span>
                {conversation.todoist_task_id && (
                  <>
                    <span>•</span>
                    <div className="flex items-center gap-1">
                      <Link className="h-3 w-3" />
                      <span>Linked task</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 hover:bg-muted rounded-md transition-colors"
              title="Conversation settings"
            >
              <Settings className="h-5 w-5" />
            </button>
            <button className="p-2 hover:bg-muted rounded-md transition-colors">
              <MoreVertical className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tools indicator */}
        {conversation.tools && conversation.tools.length > 0 && (
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Active tools:</span>
            <div className="flex gap-1">
              {conversation.tools.map((tool: any) => (
                <span key={tool.tool_name} className="inline-flex items-center px-2 py-1 bg-muted rounded text-xs">
                  🛠️ {tool.tool_name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">💭</div>
            <h3 className="text-lg font-semibold mb-2">Start the conversation</h3>
            <p className="text-muted-foreground">Ask me anything about your tasks, projects, or just have a chat!</p>
          </div>
        ) : (
          messages.map((message) => (
            <div key={message.id} className="group">
              {message.role === "user" ? (
                /* User message */
                <div className="flex justify-end">
                  <div className="bg-primary text-white p-4 rounded-lg max-w-[80%] relative group">
                    <div className="whitespace-pre-wrap">
                      {(message as any).content || (message as any).textContent || ""}
                    </div>
                    <div className="absolute -top-2 -left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => copyMessage((message as any).content || (message as any).textContent || "")}
                        className="p-1 bg-background border border-border rounded text-xs hover:bg-muted text-foreground"
                        title="Copy message"
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="text-xs opacity-70 mt-2">
                      {new Date((message as any).createdAt || Date.now()).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ) : (
                /* AI response */
                <div className="flex justify-start">
                  <div className="flex gap-3 max-w-[80%]">
                    <PersonaAvatar persona={conversation.primary_persona} size="sm" />
                    <div className="bg-muted p-4 rounded-lg relative group flex-1">
                      <div className="whitespace-pre-wrap">
                        {(message as any).content || (message as any).textContent || ""}
                      </div>

                      <MessageActions
                        message={message}
                        onCopy={() => copyMessage((message as any).content || (message as any).textContent || "")}
                        onRegenerate={() => regenerateMessage(message.id)}
                      />

                      <div className="text-xs text-muted-foreground mt-2">
                        {new Date((message as any).createdAt || Date.now()).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}

        {/* Streaming indicator */}
        {status === "streaming" && (
          <div className="flex justify-start">
            <div className="flex gap-3 max-w-[80%]">
              <PersonaAvatar persona={conversation.primary_persona} size="sm" />
              <div className="bg-muted p-4 rounded-lg">
                <StreamingIndicator />
              </div>
            </div>
          </div>
        )}

        {/* Error display */}
        {error && (
          <div className="flex justify-center">
            <div className="bg-red-100 border border-red-300 text-red-700 p-4 rounded-lg max-w-[80%]">
              <div className="flex items-start gap-2">
                <span className="text-red-500">⚠️</span>
                <div className="flex-1">
                  <div className="font-medium">Error occurred</div>
                  <div className="text-sm mt-1">{error.message}</div>
                  <button
                    onClick={() => {
                      /* Error clears automatically */
                    }}
                    className="text-sm underline hover:no-underline mt-2"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (input.trim()) {
            void sendMessage({ role: "user", text: input })
            setInput("")
          }
        }}
        className="border-t border-border p-4"
      >
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message... (Press Enter to send, Shift+Enter for new line)"
              className="w-full p-3 pr-12 border border-border rounded-lg resize-none focus:outline-hidden focus:ring-2 focus:ring-primary min-h-[52px] max-h-32"
              rows={1}
              disabled={status === "streaming"}
              style={{
                height: "auto",
                minHeight: "52px",
              }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement
                target.style.height = "auto"
                target.style.height = Math.min(target.scrollHeight, 128) + "px"
              }}
            />

            <button
              type="submit"
              disabled={!input?.trim() || status === "streaming"}
              className="absolute right-3 bottom-3 p-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Quick suggestions */}
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setInput("Break down this task into subtasks")}
            className="px-3 py-1 text-xs bg-muted hover:bg-muted/80 rounded-full transition-colors"
          >
            💡 Break down task
          </button>
          <button
            type="button"
            onClick={() => setInput("Estimate how long this will take")}
            className="px-3 py-1 text-xs bg-muted hover:bg-muted/80 rounded-full transition-colors"
          >
            ⏱️ Estimate time
          </button>
          <button
            type="button"
            onClick={() => setInput("Help me prioritize my tasks")}
            className="px-3 py-1 text-xs bg-muted hover:bg-muted/80 rounded-full transition-colors"
          >
            🎯 Prioritize
          </button>
        </div>
      </form>
    </div>
  )
}
